/**
 * Admin Internal Interviews Page
 *
 * Full CRUD table for managing team member interview records. The internal
 * interview period ended 2026-10-07; this admin console is now the only way
 * to view or edit this data (the public tracker route was removed — see
 * docs/working-docs/ENGINEERING_DECISIONS.md's 2026-10-07 entry). Data is
 * retained.
 * Route: /internal-interviews
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PencilSimple as Pencil,
  Plus,
  ArrowsClockwise as RefreshCw,
  MagnifyingGlass as Search,
  Trash,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Spinner } from "@/components/ui/Spinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import {
  useAdminInterviews,
  useCreateTeamMember,
  useDeleteTeamMember,
  useUpdateTeamMember,
} from "@/hooks/useInterviews";
import type {
  CreateTeamMemberInput,
  TeamMember,
  UpdateTeamMemberInput,
} from "@/services/interviews";
import { cn } from "@/utils/cn";

const STATUS_OPTIONS = ["", "Contacted", "Scheduled", "Interviewed"];
const REWARD_OPTIONS = ["0/3", "1/3", "2/3", "3/3"];

/** Labels shown in admin tooltips / form context. */
const REWARD_LABELS: Record<string, string> = {
  "0/3": "No rewards yet",
  "1/3": "Joined interview",
  "2/3": "First post made",
  "3/3": "Quarter Champion",
};

const STATUS_VARIANTS: Record<
  string,
  "default" | "success" | "warning" | "outline"
> = {
  Interviewed: "success",
  Contacted: "warning",
  Scheduled: "default",
};

// ── Form Dialog ────────────────────────────────────────────

interface MemberFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member?: TeamMember | null;
}

function MemberFormDialog({ open, onOpenChange, member }: MemberFormProps) {
  const isEdit = !!member;
  const createMutation = useCreateTeamMember();
  const updateMutation = useUpdateTeamMember();

  const [form, setForm] = useState<CreateTeamMemberInput>({
    team: "",
    name: "",
    role: "",
    email: "",
    whatsApp: "",
    status: "",
    rewardGiven: "0/3",
    notes: "",
  });

  // Sync form when member changes
  const resetForm = useCallback((m?: TeamMember | null) => {
    if (m) {
      setForm({
        team: m.team,
        name: m.name,
        role: m.role,
        email: m.email,
        whatsApp: m.whatsApp,
        status: m.status,
        rewardGiven: m.rewardGiven,
        notes: m.notes,
      });
    } else {
      setForm({
        team: "",
        name: "",
        role: "",
        email: "",
        whatsApp: "",
        status: "",
        rewardGiven: "0/3",
        notes: "",
      });
    }
  }, []);

  // Sync form whenever the dialog opens or the member being edited changes.
  // Using useEffect instead of handleOpenChange because Radix Dialog only fires
  // onOpenChange on internal events (Escape, overlay click), not when the parent
  // programmatically sets open={true}.
  useEffect(() => {
    if (open) resetForm(member);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, member]);

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      if (isEdit && member) {
        const input: UpdateTeamMemberInput = {};
        for (const [key, value] of Object.entries(form)) {
          if (value !== (member as unknown as Record<string, unknown>)[key]) {
            (input as Record<string, unknown>)[key] = value;
          }
        }
        await updateMutation.mutateAsync({ id: member.id, input });
      } else {
        await createMutation.mutateAsync(form);
      }
      onOpenChange(false);
    } catch {
      // Mutation error is available via createMutation.error / updateMutation.error
      // and displayed below the form
    }
  }

  const fieldClass =
    "w-full rounded-lg border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Member" : "Add Member"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update this team member's details."
              : "Add a new team member to the interviews tracker."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Name *
              </label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={fieldClass}
                placeholder="John Doe"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Team *
              </label>
              <input
                required
                value={form.team}
                onChange={(e) => setForm({ ...form, team: e.target.value })}
                className={fieldClass}
                placeholder="Engineering"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Role
              </label>
              <input
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className={fieldClass}
                placeholder="Backend Developer"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Email
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={fieldClass}
                placeholder="john@charisol.io"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                WhatsApp
              </label>
              <input
                value={form.whatsApp}
                onChange={(e) => setForm({ ...form, whatsApp: e.target.value })}
                className={fieldClass}
                placeholder="+234..."
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Status
              </label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className={fieldClass}
                aria-label="Status"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s || "Not contacted"}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Reward Given
              </label>
              <select
                value={form.rewardGiven}
                onChange={(e) =>
                  setForm({ ...form, rewardGiven: e.target.value })
                }
                className={fieldClass}
                aria-label="Reward given"
              >
                {REWARD_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r} — {REWARD_LABELS[r]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Researcher's summary — admin only, never shown on public page */}
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Researcher's Summary
              <span className="ml-1.5 text-[10px] font-normal text-muted-foreground">
                (admin only)
              </span>
            </label>
            <textarea
              value={form.notes ?? ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={4}
              maxLength={5000}
              className={`${fieldClass} resize-y`}
              placeholder="Add researcher's summary, follow-up actions, or any relevant context…"
            />
          </div>

          {/* Mutation error display */}
          {(createMutation.error || updateMutation.error) && (
            <p className="text-sm text-destructive">
              {(createMutation.error || updateMutation.error)?.message ||
                "Something went wrong. Please try again."}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? <Spinner size="sm" className="mr-1.5" /> : null}
              {isEdit ? "Save Changes" : "Add Member"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete Confirmation ────────────────────────────────────

interface DeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: TeamMember | null;
}

function DeleteConfirmDialog({
  open,
  onOpenChange,
  member,
}: DeleteDialogProps) {
  const deleteMutation = useDeleteTeamMember();

  async function handleDelete() {
    if (!member) return;
    try {
      await deleteMutation.mutateAsync(member.id);
      onOpenChange(false);
    } catch {
      // Error shown via deleteMutation.error if needed
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Team Member</DialogTitle>
          <DialogDescription>
            Are you sure you want to remove <strong>{member?.name}</strong> from
            the tracker? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? (
              <Spinner size="sm" className="mr-1.5" />
            ) : null}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ──────────────────────────────────────────────

export function AdminInterviewsPage() {
  const {
    data: members,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useAdminInterviews();

  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [teamFilter, setTeamFilter] = useState("all");

  // Dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [editMember, setEditMember] = useState<TeamMember | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteMember, setDeleteMember] = useState<TeamMember | null>(null);

  const teams = useMemo(() => {
    if (!members) return [];
    return [...new Set(members.map((m) => m.team))].sort();
  }, [members]);

  const filtered = useMemo(() => {
    if (!members) return [];
    return members.filter((m) => {
      const matchesTeam = teamFilter === "all" || m.team === teamFilter;
      const q = search.toLowerCase();
      const matchesSearch =
        !q ||
        m.name.toLowerCase().includes(q) ||
        m.role.toLowerCase().includes(q) ||
        m.email.toLowerCase().includes(q);
      return matchesTeam && matchesSearch;
    });
  }, [members, teamFilter, search]);

  // Overview counts — the one thing the now-removed public tracker showed
  // that this page didn't: a quick read of where the whole roster stands,
  // not just the filtered table below.
  const counts = useMemo(() => {
    if (!members) return { total: 0, interviewed: 0, contacted: 0 };
    return {
      total: members.length,
      interviewed: members.filter((m) => m.status === "Interviewed").length,
      contacted: members.filter(
        (m) => m.status === "Contacted" || m.status === "Scheduled"
      ).length,
    };
  }, [members]);

  function openCreate() {
    setEditMember(null);
    setFormOpen(true);
  }

  function openEdit(m: TeamMember) {
    setEditMember(m);
    setFormOpen(true);
  }

  function openDelete(m: TeamMember) {
    setDeleteMember(m);
    setDeleteOpen(true);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
            Internal Interviews
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage team member interview tracking for Spark adoption.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw
              className={cn("mr-1.5 h-3.5 w-3.5", isFetching && "animate-spin")}
            />
            Refresh
          </Button>
          <Button size="sm" onClick={openCreate}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add Member
          </Button>
        </div>
      </div>

      {/* Overview counts */}
      {!isLoading && members && (
        <div className="flex flex-wrap gap-3 text-sm">
          <span className="rounded-md bg-muted px-3 py-1">
            <span className="font-medium">{counts.total}</span> members
          </span>
          <span className="rounded-md bg-success/10 px-3 py-1 text-success">
            <span className="font-medium">{counts.interviewed}</span>{" "}
            interviewed
          </span>
          <span className="rounded-md bg-warning/10 px-3 py-1 text-warning">
            <span className="font-medium">{counts.contacted}</span>{" "}
            contacted/scheduled
          </span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, role, or email..."
            aria-label="Search by name, role, or email"
            className="w-full rounded-lg border border-border bg-card py-2.5 pl-10 pr-4 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <select
          value={teamFilter}
          onChange={(e) => setTeamFilter(e.target.value)}
          className="rounded-lg border border-border bg-card px-3 py-2.5 text-sm"
          aria-label="Filter by team"
        >
          <option value="all">All teams</option>
          {teams.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <Spinner size="md" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-center text-sm text-destructive">
          Failed to load team members.
        </div>
      ) : filtered.length === 0 ? (
        <p className="py-12 text-center text-sm text-muted-foreground">
          No team members found.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border/50">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Name</th>
                <th className="hidden px-4 py-3 sm:table-cell">Team</th>
                <th className="hidden px-4 py-3 md:table-cell">Role</th>
                <th className="hidden px-4 py-3 md:table-cell">Email</th>
                <th className="px-4 py-3">Status</th>
                <th className="hidden px-4 py-3 lg:table-cell">Reward</th>
                <th className="hidden px-4 py-3 xl:table-cell">Summary</th>
                <th className="px-4 py-3 w-20 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {filtered.map((m) => (
                <tr
                  key={m.id}
                  className="cursor-pointer hover:bg-accent/50 transition-colors"
                  onClick={() => navigate(`/internal-interviews/${m.id}`)}
                >
                  <td className="px-4 py-3 font-medium">{m.name}</td>
                  <td className="hidden px-4 py-3 text-muted-foreground sm:table-cell">
                    {m.team}
                  </td>
                  <td className="hidden px-4 py-3 text-muted-foreground md:table-cell">
                    {m.role || "—"}
                  </td>
                  <td className="hidden px-4 py-3 text-muted-foreground md:table-cell">
                    {m.email || "—"}
                  </td>
                  <td className="px-4 py-3">
                    {m.status ? (
                      <Badge variant={STATUS_VARIANTS[m.status] ?? "outline"}>
                        {m.status}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="hidden px-4 py-3 text-muted-foreground lg:table-cell">
                    {m.rewardGiven === "0/3" ? "—" : m.rewardGiven}
                  </td>
                  <td className="hidden px-4 py-3 xl:table-cell max-w-xs">
                    {m.notes ? (
                      <p className="line-clamp-2 whitespace-pre-wrap text-sm text-muted-foreground">
                        {m.notes}
                      </p>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEdit(m);
                        }}
                        className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                        aria-label={`Edit ${m.name}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openDelete(m);
                        }}
                        className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                        aria-label={`Delete ${m.name}`}
                      >
                        <Trash className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Dialogs */}
      <MemberFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        member={editMember}
      />
      <DeleteConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        member={deleteMember}
      />
    </div>
  );
}
